/**
 * Phase 21 — Admin Partner Ranking & Performance dashboard.
 *
 * Route: /admin/partners/rankings
 *
 * PINNED USER RULE (Feb 2026):
 *   Phase 21 is reporting / ranking only. No partner / booking /
 *   commission / opportunity / follow-up / workflow / pricing /
 *   white-label / partner-status / public-lead-allocation row is
 *   mutated. No emails / SMS / public pages / notifications are sent.
 *   The score is an admin-only indicator; it is never used to
 *   auto-change partner status, preferred flag, payouts or
 *   allocations.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft, RefreshCw, AlertTriangle, Loader2, ShieldAlert,
  BarChart3, Trophy, Download, Info, ChevronRight, Activity,
} from "lucide-react";
import { toast } from "sonner";
import api, { API_BASE, formatApiError } from "@/lib/api";
import { PortalShell } from "@/components/PortalShell";


const WINDOWS = [
  { slug: "last_7d",    label: "Last 7 days" },
  { slug: "last_30d",   label: "Last 30 days" },
  { slug: "this_month", label: "This month" },
  { slug: "all_time",   label: "All time" },
];


const FLAG_LABELS = {
  no_bookings_in_30d:    "No bookings in 30 days",
  high_stalled_workflow: "High stalled workflow",
  commission_disabled:   "Commission disabled",
  white_label_disabled:  "White label disabled",
  kits_overdue:          "Kits overdue",
  reports_pending:       "Reports pending",
};


function ScoreBadge({ score, band }) {
  const color =
    band === "Top performer" ? "bg-emerald-100 text-emerald-800 border-emerald-200" :
    band === "Solid"         ? "bg-sky-100 text-sky-800 border-sky-200" :
    band === "Developing"    ? "bg-amber-100 text-amber-800 border-amber-200" :
    band === "Attention"     ? "bg-orange-100 text-orange-800 border-orange-200" :
                               "bg-slate-100 text-slate-600 border-slate-200";
  return (
    <span
      data-testid="ph21-score-badge"
      className={`inline-flex items-center gap-1 text-[11px] font-bold px-2 py-1 rounded-full border ${color}`}
    >
      <Trophy className="w-3 h-3" /> {score} · {band}
    </span>
  );
}


function MoneyCell({ value }) {
  const n = Number(value || 0);
  return <span className="font-mono text-xs">${n.toFixed(2)}</span>;
}


function FlagChip({ slug }) {
  return (
    <span
      data-testid={`ph21-flag-${slug}`}
      className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-800 border border-amber-200"
    >
      <AlertTriangle className="w-2.5 h-2.5" /> {FLAG_LABELS[slug] || slug}
    </span>
  );
}


function TotalsStrip({ totals, window }) {
  const items = [
    { label: "Eligible partners",      value: totals.eligible_partners },
    { label: "Bookings (window)",      value: totals.total_bookings },
    { label: "Bookings 30d",           value: totals.total_bookings_last_30d },
    { label: "Published results",      value: totals.total_published_results },
    { label: "Partner revenue",        value: `$${(totals.total_partner_revenue || 0).toFixed(2)}` },
    { label: "Opportunity value",      value: `$${(totals.total_opportunity_value || 0).toFixed(2)}` },
    { label: "Stalled workflow",       value: totals.total_stalled_workflow },
    { label: "Kits overdue",           value: totals.total_kits_overdue },
    { label: "Review-required (excl.)", value: totals.review_required_bookings },
  ];
  return (
    <section
      data-testid="ph21-totals-strip"
      className="rounded-2xl bg-white border border-slate-200 p-4 mt-4 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3"
    >
      {items.map((it) => (
        <div key={it.label} className="text-xs">
          <div className="text-[10px] uppercase tracking-wider text-slate-500">{it.label}</div>
          <div className="font-bold text-slate-800 text-sm mt-0.5">{it.value}</div>
        </div>
      ))}
      <div className="text-xs col-span-2 sm:col-span-3 lg:col-span-5 text-[10px] text-slate-400 italic">
        Window: <b>{window}</b>. Direct Aware bookings and review-required
        region-match bookings are deliberately excluded from per-partner
        rankings.
      </div>
    </section>
  );
}


function ScoreExplanationPanel({ explanation, open, onClose }) {
  if (!open) return null;
  return (
    <div
      data-testid="ph21-score-explanation-panel"
      className="rounded-2xl bg-slate-50 border border-slate-200 p-5 mt-4"
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-[0.15em] font-bold text-slate-700">
            {explanation.label}
          </div>
          <div className="text-[10px] text-slate-500 mt-1">
            {explanation.audience}
          </div>
        </div>
        <button
          onClick={onClose}
          data-testid="ph21-close-explanation"
          className="text-xs font-semibold px-2 py-1 rounded-full bg-slate-100 hover:bg-slate-200"
        >
          Close
        </button>
      </div>
      <p className="text-xs text-slate-700 mt-3">{explanation.disclaimer}</p>
      <ul className="mt-4 space-y-2">
        {explanation.components.map((c) => (
          <li
            key={c.slug}
            data-testid={`ph21-comp-${c.slug}`}
            className="text-xs text-slate-700"
          >
            <span className="font-semibold">{c.label}</span>
            <span className="text-slate-500"> · max {c.max} pts</span>
            <span className="block text-[11px] text-slate-500">{c.explain}</span>
          </li>
        ))}
      </ul>
      <div className="mt-4 text-[10px] text-slate-500 italic">
        Bands: {explanation.bands.map((b) => `≥ ${b.min} → ${b.label}`).join(" · ")}.
      </div>
    </div>
  );
}


function RankingsTable({ rows }) {
  if (!rows.length) {
    return (
      <div
        data-testid="ph21-empty"
        className="rounded-2xl bg-white border border-dashed border-slate-200 p-10 text-center text-slate-400"
      >
        No eligible PWS partners in this window.
      </div>
    );
  }
  return (
    <section
      data-testid="ph21-rankings-table"
      className="rounded-2xl bg-white border border-slate-200 mt-4 overflow-x-auto"
    >
      <table className="w-full text-xs">
        <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
          <tr>
            <th className="px-3 py-2 text-left">#</th>
            <th className="px-3 py-2 text-left">Partner</th>
            <th className="px-3 py-2 text-left">Region</th>
            <th className="px-3 py-2 text-left">Status</th>
            <th className="px-3 py-2 text-left">Score</th>
            <th className="px-3 py-2 text-right">Total</th>
            <th className="px-3 py-2 text-right">30d</th>
            <th className="px-3 py-2 text-right">Published</th>
            <th className="px-3 py-2 text-right">Opp. value</th>
            <th className="px-3 py-2 text-right">FU done/total</th>
            <th className="px-3 py-2 text-right">Stalled</th>
            <th className="px-3 py-2 text-right">Commission</th>
            <th className="px-3 py-2 text-left">Last activity</th>
            <th className="px-3 py-2 text-left">Flags</th>
            <th className="px-3 py-2"></th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr
              key={r.partner_id}
              data-testid={`ph21-row-${r.partner_id}`}
              className="border-t border-slate-100 hover:bg-slate-50"
            >
              <td className="px-3 py-2 font-bold">{r.rank}</td>
              <td className="px-3 py-2">
                <div className="font-semibold text-slate-800">{r.business_name || "—"}</div>
                <div className="text-[10px] text-slate-500 font-mono">{r.partner_slug || "—"}</div>
              </td>
              <td className="px-3 py-2 text-slate-700">{r.franchise_region || "—"}</td>
              <td className="px-3 py-2">
                <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-100">
                  {r.partner_status}
                </span>
              </td>
              <td className="px-3 py-2">
                <ScoreBadge score={r.score} band={r.score_band} />
              </td>
              <td className="px-3 py-2 text-right font-mono">{r.metrics.total_bookings}</td>
              <td className="px-3 py-2 text-right font-mono">{r.metrics.bookings_last_30d}</td>
              <td className="px-3 py-2 text-right font-mono">{r.metrics.published_results}</td>
              <td className="px-3 py-2 text-right"><MoneyCell value={r.metrics.estimated_opportunity_value} /></td>
              <td className="px-3 py-2 text-right font-mono">
                {r.metrics.follow_ups_completed}/{r.metrics.follow_ups_total}
              </td>
              <td className="px-3 py-2 text-right font-mono">{r.metrics.stalled_workflow_count}</td>
              <td className="px-3 py-2 text-right">
                <div className="text-[10px] text-emerald-700">Paid <MoneyCell value={r.metrics.commission_paid_dollars} /></div>
                <div className="text-[10px] text-amber-700">Payable <MoneyCell value={r.metrics.commission_payable_dollars} /></div>
              </td>
              <td className="px-3 py-2 text-[10px] text-slate-600">
                {r.metrics.last_activity_at ? r.metrics.last_activity_at.slice(0, 10) : "—"}
              </td>
              <td className="px-3 py-2">
                <div className="flex flex-wrap gap-1">
                  {r.attention_flags.length === 0 && (
                    <span className="text-[10px] text-emerald-600 italic">none</span>
                  )}
                  {r.attention_flags.map((slug) => (
                    <FlagChip key={slug} slug={slug} />
                  ))}
                </div>
              </td>
              <td className="px-3 py-2 text-right">
                <Link
                  to={`/admin/partners/network?focus=${encodeURIComponent(r.partner_id)}`}
                  data-testid={`ph21-open-detail-${r.partner_id}`}
                  className="inline-flex items-center gap-1 text-[11px] font-semibold text-blue-700 hover:text-blue-800"
                >
                  Open <ChevronRight className="w-3 h-3" />
                </Link>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}


function IneligibleSection({ ineligible }) {
  if (!ineligible.length) return null;
  return (
    <section
      data-testid="ph21-ineligible-section"
      className="rounded-2xl bg-slate-50 border border-slate-200 p-4 mt-6"
    >
      <div className="text-[11px] uppercase tracking-[0.15em] font-bold text-slate-700 mb-2 flex items-center gap-1.5">
        <Activity className="w-3.5 h-3.5" /> Ineligible / not ranked
        <span className="ml-2 text-[10px] font-normal text-slate-500 italic">
          ({ineligible.length} partners — draft / inactive / suspended)
        </span>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
        {ineligible.slice(0, 30).map((p) => (
          <div
            key={p.partner_id}
            data-testid={`ph21-ineligible-${p.partner_id}`}
            className="text-xs text-slate-700 border border-slate-200 rounded-md px-2 py-1.5 bg-white"
          >
            <div className="font-semibold truncate">{p.business_name || "—"}</div>
            <div className="text-[10px] text-slate-500">
              {p.partner_slug || "—"} · {p.partner_status}
            </div>
          </div>
        ))}
      </div>
      {ineligible.length > 30 && (
        <div className="mt-2 text-[10px] text-slate-500 italic">
          {ineligible.length - 30} more partners hidden.
        </div>
      )}
    </section>
  );
}


// ── Page ────────────────────────────────────────────────────────────────
export default function AdminPWSPartnerRankings() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [window, setWindow] = useState("last_30d");
  const [summary, setSummary] = useState(null);
  const [showExplain, setShowExplain] = useState(false);

  const load = useCallback(async (win) => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.get(
        `/admin/pws-partner-rankings/summary?window=${encodeURIComponent(win)}`,
      );
      setSummary(data);
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(window); }, [load, window]);

  const onCsvDownload = useCallback(async () => {
    try {
      const res = await api.get(
        `/admin/pws-partner-rankings/export.csv?window=${encodeURIComponent(window)}`,
        { responseType: "blob" },
      );
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `pws_partner_rankings_${window}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("CSV downloaded");
    } catch (e) {
      toast.error(formatApiError(e) || "Failed to download CSV");
    }
  }, [window]);

  const explanation = summary?.score_explanation;

  return (
    <PortalShell>
      <div
        data-testid="admin-pws-partner-rankings-page"
        className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8"
      >
        <Link
          to="/admin/partners/network"
          data-testid="ph21-back-to-network"
          className="inline-flex items-center gap-1.5 text-xs font-semibold text-blue-700 hover:text-blue-800"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> Back to PWS Network
        </Link>

        <div className="mt-3 flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-cyan-700">
              Aware Partner Network · Phase 21
            </div>
            <h1 className="font-display mt-2 text-3xl font-bold text-slate-900">
              Partner Ranking &amp; Performance
            </h1>
            <p className="text-slate-500 mt-1 text-sm max-w-3xl">
              Reporting-only dashboard for Aware admins. The score is an
              internal indicator — never used to automatically change
              partner status, preferred flag, commission, payouts or
              public allocation.
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={() => load(window)}
              data-testid="ph21-refresh-btn"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-slate-100 text-slate-700 hover:bg-slate-200"
            >
              <RefreshCw className="w-3.5 h-3.5" /> Refresh
            </button>
            <button
              onClick={() => setShowExplain((v) => !v)}
              data-testid="ph21-toggle-explain"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-slate-100 text-slate-700 hover:bg-slate-200"
            >
              <Info className="w-3.5 h-3.5" /> Score explanation
            </button>
            <button
              onClick={onCsvDownload}
              data-testid="ph21-download-csv-btn"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-800 hover:bg-emerald-200"
            >
              <Download className="w-3.5 h-3.5" /> Download CSV
            </button>
          </div>
        </div>

        {/* Safety banner */}
        <div
          data-testid="ph21-safety-banner"
          className="mt-5 rounded-xl bg-cyan-50 border border-cyan-200 p-4 flex items-start gap-3 text-sm text-cyan-900"
        >
          <ShieldAlert className="w-5 h-5 mt-0.5 flex-shrink-0" />
          <div>
            <strong>Reporting / ranking only.</strong> No emails / SMS /
            social posts / public pages / partner notifications are sent.
            No partner / booking / commission / opportunity / follow-up /
            workflow / pricing / white-label / partner-status row is
            mutated. The score is an admin-only indicator and is never
            used to automatically change anything.
          </div>
        </div>

        {/* Window picker */}
        <section
          data-testid="ph21-window-picker"
          className="rounded-2xl bg-white border border-slate-200 p-4 mt-4 flex items-center gap-2 flex-wrap"
        >
          <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-[0.15em] font-bold text-slate-700 mr-2">
            <BarChart3 className="w-3.5 h-3.5 text-cyan-700" /> Window
          </div>
          {WINDOWS.map((w) => (
            <button
              key={w.slug}
              type="button"
              data-testid={`ph21-window-${w.slug}`}
              onClick={() => setWindow(w.slug)}
              className={`text-xs font-semibold px-3 py-1.5 rounded-full border ${
                window === w.slug
                  ? "bg-cyan-700 text-white border-cyan-700"
                  : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"
              }`}
            >
              {w.label}
            </button>
          ))}
        </section>

        {/* Explanation panel */}
        {explanation && (
          <ScoreExplanationPanel
            explanation={explanation}
            open={showExplain}
            onClose={() => setShowExplain(false)}
          />
        )}

        {loading ? (
          <div
            data-testid="ph21-loading"
            className="mt-8 rounded-2xl bg-white border border-slate-200 p-10 flex items-center gap-2 text-slate-400 justify-center"
          >
            <Loader2 className="w-5 h-5 animate-spin" /> Loading rankings…
          </div>
        ) : error ? (
          <div
            data-testid="ph21-error"
            className="mt-8 rounded-xl bg-rose-50 border border-rose-200 p-5 text-sm text-rose-800"
          >
            <div className="font-semibold mb-1 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4" /> Failed to load
            </div>
            <div className="text-xs">{error}</div>
          </div>
        ) : summary ? (
          <>
            <TotalsStrip totals={summary.totals} window={summary.window} />
            <RankingsTable rows={summary.rows} />
            <IneligibleSection ineligible={summary.ineligible} />
          </>
        ) : null}
      </div>
    </PortalShell>
  );
}
